// The reimbursement figure the receipt shows is the PAYOUT owed to the property for the period -
// the same stored monthly Payout that Property Details → Highlights → "Reimbursed <Month>" reads
// (Casa de Helix June 2026 = $2,516.15). The backend resolves it and returns it as
// `reimbursementAmount` together with `payoutComputed`; the FE must not derive, sum, or substitute
// anything else.
//
// It must NEVER fall back to the payments collected in the period ($2,614.87 for that same
// property/month) or to charge recovery - those are different, larger numbers, and showing one of
// them under the word "reimbursement" is the bug this replaced. When the payout is not computed
// yet the tile shows no amount and says so.

/** The reimbursement amount to display, or null when the period's payout is not computed yet. */
export const pickReceiptReimbursement = function () {
  let receipt = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const isPayout = Boolean(receipt === null || receipt === void 0 ? void 0 : receipt.payoutComputed) && (receipt === null || receipt === void 0 ? void 0 : receipt.reimbursementAmount) != null;
  return {
    value: isPayout ? receipt.reimbursementAmount : null,
    isPayout
  };
};

/**
 * Collected from residents in the period but not paid out. Overwhelmingly the convenience fee
 * Livable retains on card payments: over prod July 2026, 560 of the 690 properties with payments
 * (81%) had a gap that was EXACTLY the fees, and fees were 72% of the gap in dollars. The rest is
 * amounts held against residents in arrears and prior-period ledger activity. Null until the payout
 * is computed.
 */
export const pickReceiptWithheld = function () {
  let receipt = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return receipt !== null && receipt !== void 0 && receipt.payoutComputed && (receipt === null || receipt === void 0 ? void 0 : receipt.withheldOrAdjusted) != null ? receipt.withheldOrAdjusted : null;
};

/**
 * The fee/other split behind `pickReceiptWithheld`, or null when this client is talking to a
 * backend deployed before the split existed - in that case the tile shows the total alone rather
 * than inventing a breakdown.
 */
export const pickReceiptWithheldBreakdown = function () {
  var _receipt$otherAdjustm;
  let receipt = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  if (!(receipt !== null && receipt !== void 0 && receipt.payoutComputed) || (receipt === null || receipt === void 0 ? void 0 : receipt.processingFeesRetained) == null) return null;
  return {
    processingFees: receipt.processingFeesRetained,
    other: (_receipt$otherAdjustm = receipt.otherAdjustments) !== null && _receipt$otherAdjustm !== void 0 ? _receipt$otherAdjustm : 0
  };
};

/** Haley's wording, kept verbatim. Used when the backend has not sent the fee/other split. */
export const WITHHELD_FOOTNOTE = 'Withheld for processing fees, or resident negative balances related to refunds or ledger adjustments';

/**
 * The gap is nearly always the convenience fee, so lead with that and only mention the rest when
 * there IS a rest. A backend deployed before the split sends no breakdown - fall back to the plain
 * sentence rather than invent a split. The formatter is injected so this module stays free of
 * `src/`-aliased imports - jest has no alias for them.
 */
export const renderWithheldFootnote = (receipt, formatCurrency) => {
  const breakdown = pickReceiptWithheldBreakdown(receipt);
  if (!breakdown) return WITHHELD_FOOTNOTE;
  if (breakdown.other === 0) {
    return "Processing fees retained on card payments: ".concat(formatCurrency(breakdown.processingFees));
  }
  return "".concat(formatCurrency(breakdown.processingFees), " processing fees, ").concat(formatCurrency(breakdown.other), " resident balances or ledger adjustments");
};

/**
 * The Payouts table only holds the two most recent months, so a 12-month history read from it alone
 * was mostly "Not yet computed" for amounts that were never coming. The backend now falls back to
 * what the property was actually PAID (the settled payout ledger, which goes back years), so these
 * rows carry real figures.
 *
 * `payoutPredatesHistory` is what is left: a period older than anything either source knows about,
 * which will never fill in. That is "Not available". A gap NEWER than that is genuinely pending.
 */
export const renderPeriodReimbursement = (period, formatCurrency) => {
  // payoutComputed and a null amount should never coexist, but the amount is typed nullable, so
  // trust the amount rather than the flag: a formatter handed null would throw.
  if (period !== null && period !== void 0 && period.payoutComputed && (period === null || period === void 0 ? void 0 : period.reimbursementAmount) != null) {
    return formatCurrency(period.reimbursementAmount);
  }
  return period !== null && period !== void 0 && period.payoutPredatesHistory ? 'Not available' : 'Not yet computed';
};